import React from "react";
import { AlertCircle, Phone, Mail, Building2, MapPin, MessageSquareText, Cpu, User } from "lucide-react";

const CriticalLeads = ({ criticalLeads = [], loading = false, onLeadClick, onViewAllClick, calculateDaysSince, parseAndExtractValue }) => {
  const getIconForQuestion = (questionLabel) => {
    const label = (questionLabel || "").trim().toLowerCase();
    if (label.includes("phone") || label.includes("mobile") || label.includes("contact")) return <Phone className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />;
    if (label.includes("email")) return <Mail className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />;
    if (label.includes("company") || label.includes("business")) return <Building2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />;
    if (label.includes("address")) return <MapPin className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />;
    if (label.includes("remark") || label.includes("description")) return <MessageSquareText className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />;
    if (label.includes("generated") || label.includes("server")) return <Cpu className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />;
    if (label.includes("designation")) return <User className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />;
    return <User className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />;
  };

  return (
    <div className="bg-white rounded-lg p-6 relative" style={{ boxShadow: "rgba(0, 0, 0, 0.2) 0px 2px 1px -1px, rgba(0, 0, 0, 0.14) 0px 1px 1px 0px, rgba(0, 0, 0, 0.12) 0px 1px 3px 0px", minHeight: "600px" }}>
      {loading && (
        <div className="absolute inset-0 bg-white bg-opacity-90 rounded-lg flex justify-center items-center z-10">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#dc3545]"></div>
            <p className="ml-3 text-gray-600">Loading critical leads...</p>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <AlertCircle className="w-6 h-6" style={{ color: "#c12328" }} />
          Critical Ageing Leads
        </h2>
        <span className="text-sm font-semibold px-3 py-1 rounded-full" style={{ backgroundColor: "#F69193", color: "#c12328" }}>{criticalLeads.length} Leads</span>
      </div>
      <div className="space-y-3 max-h-[600px] overflow-y-auto">
        {criticalLeads.length === 0 && !loading ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <p className="text-gray-500">No critical leads found.</p>
          </div>
        ) : (
          criticalLeads.map((lead) => {
            const displayFields = lead.leadFormDetails?.questions?.filter((q) => q.toView && q.userResponse?.response) || [];
            const mainField = displayFields[0];
            const remainingFields = displayFields.slice(1);
            const daysSince = calculateDaysSince(lead);
            return (
              <div key={lead.id} className="border rounded-lg p-4 hover:shadow-md transition-all cursor-pointer bg-white" style={{ borderColor: lead.stage?.stageName === "In Progress" && daysSince > 35 ? "#c12328" : "#e5e7eb", borderWidth: lead.stage?.stageName === "In Progress" && daysSince > 35 ? "2px" : "1px" }}>
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
                      <h3 className="text-lg font-semibold text-gray-800 cursor-pointer hover:text-blue-600" onClick={() => onLeadClick(lead.id)}>{mainField?.userResponse?.response || lead.leadFormDetails?.name || "Lead"}</h3>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap" style={{ backgroundColor: lead.stage?.stageColor ? `#${lead.stage.stageColor.slice(2)}` : "#e5e7eb", color: lead.stage?.stageColor ? "white" : "#374151" }}>{lead.stage?.stageName || "N/A"}</span>
                        {daysSince !== null && <span style={{ borderColor: lead.stage?.stageColor ? `#${lead.stage.stageColor.slice(2)}` : "#D1D5DB" }} className="px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap bg-gray-100 text-gray-700 border">{daysSince === 1 ? "1 day in stage" : `${daysSince} days in stage`}</span>}
                        {lead.status && <span className="px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap" style={{ backgroundColor: lead.status.statusColor ? `#${lead.status.statusColor.slice(2)}` : "#e5e7eb", color: lead.status.statusColor ? "white" : "#374151" }}>{lead.status.statusName}</span>}
                      </div>
                    </div>
                    <div className="space-y-2 text-gray-600">
                      {remainingFields.map((field) => (
                        <div key={field.id} className="flex items-start gap-2">
                          <div className="flex items-center gap-1.5">
                            {getIconForQuestion(field.question)}
                            <span className="text-xs font-medium text-gray-500 whitespace-nowrap">{field.question}:</span>
                          </div>
                          <span className="text-sm text-gray-800 break-words">{parseAndExtractValue(field.userResponse?.response, field.viewValue) || "N/A"}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      {criticalLeads.length > 0 && onViewAllClick && (
        <button onClick={onViewAllClick} className="w-full mt-6 py-2 rounded-lg font-semibold transition-all hover:bg-gray-100 border" style={{ borderColor: "#c12328", color: "#c12328" }}>View All Leads</button>
      )}
    </div>
  );
};

export default CriticalLeads;